'use client';

import React from 'react';
import { Button } from '@ui/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@ui/components/ui/form';
import { useTranslations } from 'next-intl';
import { useUser } from '@/contexts/user-context';
import ChangeSettingsReminder from '@/components/onboarding/change-settings-reminder';
import ToneOfVoiceSelector from '@/components/onboarding/tone-of-voice-selector';
import LengthSelector from '@/components/onboarding/length-selector';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ToneOfVoice, Length } from '@/lib/db/schema';
import FullNameInput from '@/components/inputs/full-name-input';
import { useInputSchema } from '@/hooks/use-input-schema';

interface AccountSetupStepProps {
  onComplete: (data: { fullName: string }) => void;
  onBack: () => void;
  isLoading: boolean;
}

export function AccountSetupStep({ onComplete, onBack, isLoading }: AccountSetupStepProps) {
  const t = useTranslations('Onboarding.accountSetup');
  const tNav = useTranslations('Onboarding.navigation');
  const tCommon = useTranslations('Common');
  const tForm = useTranslations('Form');
  const tAccount = useTranslations('Account');
  const { user, updatePreferences, updateUser } = useUser();
  const { fullNameSchema } = useInputSchema();

  const formSchema = z.object({
    fullName: fullNameSchema,
    toneOfVoice: z.string().nullable(),
    length: z.string().nullable(),
  });

  type FormData = z.infer<typeof formSchema>;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: user.fullName,
      toneOfVoice: user.toneOfVoice,
      length: user.length,
    },
    mode: 'onChange',
  });

  const handleNext = form.handleSubmit((data) => {
    const payload: { fullName: string; toneOfVoice?: ToneOfVoice; length?: Length } = {
      fullName: data.fullName.trim(),
    };

    if (data.toneOfVoice) payload.toneOfVoice = data.toneOfVoice as ToneOfVoice;
    if (data.length) payload.length = data.length as Length;

    onComplete(payload);
    updateUser({
      fullName: payload.fullName,
    });
    updatePreferences({
      toneOfVoice: payload.toneOfVoice,
      length: payload.length,
    });
  });

  return (
    <div className="space-y-8 ">
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold">{t('title')}</h2>
        <p className="text-lg text-muted-foreground">{t('subtitle')}</p>
      </div>

      <Form {...form}>
        <form onSubmit={handleNext} className="flex flex-col gap-4">
          <div className="space-y-6 m-1 max-w-lg mx-auto">
            <FullNameInput form={form} />
            <div className="pt-6 border-t border-slate-100/50 space-y-4">
              <h3 className="text-lg font-medium">{tAccount('preferences')}</h3>
              <div className="flex flex-wrap gap-4">
                <FormField
                  control={form.control}
                  name="toneOfVoice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{tForm('preferences.toneOfVoice.label')}</FormLabel>
                      <ToneOfVoiceSelector value={field.value as ToneOfVoice | null} onChange={(v) => field.onChange(v || undefined)} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="length"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{tForm('preferences.length.label')}</FormLabel>
                      <LengthSelector value={field.value as Length | null} onChange={(v) => field.onChange(v || undefined)} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <ChangeSettingsReminder />
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={onBack} disabled={isLoading} size="lg">
              {tNav('previous')}
            </Button>
            <Button type="submit" disabled={isLoading} size="lg">
              {isLoading ? tCommon('states.saving') : tNav('next')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
